import { Box, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Save as SaveIcon, Undo as UndoIcon, FileUpload as ImportIcon } from '@mui/icons-material';
import type { Command } from '../../../menu/types';

/**
 * Props for GridPageHeader.
 *
 * Rendered inside MainLayout's PageSubHeader (via PageHeaderContext.headerActions),
 * on the same row as the page title — not as a standalone block above the grid.
 *
 * Save / Revert labels are intentionally hardcoded ("Save" / "Revert") rather
 * than using command.caption, because backend captions are not guaranteed to
 * be title-cased. Use caption only for the tooltip when the button is disabled.
 */
type GridPageHeaderProps = {
  /** Required by the existing call-site but unused in this version. */
  onRefresh: () => void;
  showDebug?: boolean;
  onDebugToggle?: () => void;

  /** Show Save + Revert only for inline-editable grids. */
  inlineEditable?: boolean;
  hasUnsavedChanges?: boolean;
  isBatchSaving?: boolean;
  onSave?: () => void;
  onRevert?: () => void;
  /** Umbraco command objects — used only for hintForStateDisabled tooltip text. */
  saveCommand?: Command | null;
  revertCommand?: Command | null;

  /** Single import command rendered as a plain icon button (no overflow menu). */
  importCommand?: Command | null;
  onImportClick?: (cmd: Command) => void;
};

export const GridPageHeader = ({
  inlineEditable,
  hasUnsavedChanges,
  isBatchSaving,
  onSave,
  onRevert,
  saveCommand,
  revertCommand,
  importCommand,
  onImportClick,
}: GridPageHeaderProps) => {
  const showSaveRevert = !!inlineEditable;

  if (!showSaveRevert && !importCommand) return null;

  const isSaveDisabled = !hasUnsavedChanges || !!isBatchSaving;
  const isRevertDisabled = !hasUnsavedChanges || !!isBatchSaving;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {showSaveRevert && (
        <>
          {/* ── Revert ──────────────────────────────────────── */}
          <Tooltip
            title={isRevertDisabled ? (revertCommand?.hintForStateDisabled ?? '') : ''}
            arrow
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<UndoIcon />}
                onClick={onRevert}
                disabled={isRevertDisabled}
                size="medium"
              >
                Revert
              </Button>
            </span>
          </Tooltip>

          {/* ── Save ────────────────────────────────────────── */}
          <Tooltip title={isSaveDisabled ? (saveCommand?.hintForStateDisabled ?? '') : ''} arrow>
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={isBatchSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                onClick={onSave}
                disabled={isSaveDisabled}
                size="medium"
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </>
      )}

      {/* ── Import — plain icon button, no overflow menu ───── */}
      {importCommand && (
        <Tooltip title={importCommand.caption || 'Import'} arrow>
          <IconButton
            size="medium"
            onClick={() => onImportClick?.(importCommand)}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: '7px',
            }}
          >
            <ImportIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};
